/**
 * External tool API helpers (http://localhost:5175).
 *
 * Provides functions for:
 *  - Text generation (translate, auto-tag, enrich)
 *  - Text-to-Speech
 *  - Speech-to-Text
 *  - Image generation
 *  - Conversational chat
 */

import {getAiApiBaseUrl} from './aiApiConfig';

const DEFAULT_MODEL =
  process.env.REACT_APP_OLLAMA_MODEL || 'gemma3:1b';

// ─── Low-level generate (non-streaming) ────────────────────────────

async function generate({prompt, model, signal, retries = 2}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${getAiApiBaseUrl()}/api/generate`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          model: model || DEFAULT_MODEL,
          prompt,
          stream: false,
        }),
        signal,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`generate error: ${res.status} ${body}`);
      }
      // API returns NDJSON (one JSON object per line) even with stream:false.
      // Collect all "response" fragments and concatenate them.
      const text = await res.text();
      let fullResponse = '';
      for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const obj = JSON.parse(trimmed);
          if (obj.response) fullResponse += obj.response;
        } catch (_) {/* skip malformed lines */}
      }
      return fullResponse;
    } catch (err) {
      lastError = err;
      // Don't retry if the request was intentionally aborted
      if (signal && signal.aborted) throw err;
      if (attempt < retries) {
        // Wait before retrying (500ms, 1000ms, …)
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

function extractJson(raw) {
  try {
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start !== -1 && end > start) {
      return JSON.parse(raw.slice(start, end + 1));
    }
  } catch (_) {/* ignore */}
  return null;
}

// ─── 1. Auto-fill translation ──────────────────────────────────────

export async function suggestTranslation({
  word,
  fromLang,
  toLang,
  signal,
}) {
  const prompt =
    `Translate the ${fromLang} word "${word}" into ${toLang}. ` +
    `Also provide a transliteration in English letters. ` +
    `Respond ONLY with JSON: ` +
    `{"translation": "...", "transliteration": "..."}`;
  const raw = await generate({prompt, signal});
  const parsed = extractJson(raw);
  if (parsed && parsed.translation) return parsed;
  return {translation: raw.trim(), transliteration: ''};
}

// ─── 2. Generate sample sentences ──────────────────────────────────

export async function generateSentences({wordEn, wordFa, language, signal}) {
  const isHebrew = language === 'hebrew';
  const langName = isHebrew ? 'Hebrew' : 'Persian (Farsi)';
  const hebrewConstraint = 'The sentences must be in Hebrew ' +
    '(Modern Hebrew), not Arabic, Aramaic, or Yiddish.';
  const farsiConstraint = 'The sentences must be in Persian ' +
    '(Farsi) using Persian vocabulary and grammar, not Arabic.';
  const langConstraint = isHebrew ? hebrewConstraint : farsiConstraint;
  const prompt =
    `Generate 3 sample sentences using the ${langName} word "${wordFa}" ` +
    `which means "${wordEn}" in English. ${langConstraint} ` +
    `For each sentence, provide: the sentence in ${langName} script, ` +
    `a transliteration in English letters, and the English translation. ` +
    `Respond ONLY with JSON: ` +
    `{"sentences": [{"text": "...", "transliteration": "...", ` +
    `"translation": "..."}, ...]}`;
  const raw = await generate({prompt, signal});
  const parsed = extractJson(raw);
  if (parsed && Array.isArray(parsed.sentences)) return parsed.sentences;
  return [];
}

// ─── 3. Generate Hebrew verb mnemonic ───────────────────────────────

export async function generateHebrewVerbMnemonic({
  infinitiveHe,
  transliteration,
  meaning,
  binyan,
  signal,
}) {
  const translitPart = transliteration ?
    ` (transliteration: "${transliteration}")` :
    '';
  const prompt =
    `Create a short, catchy mnemonic in English to help memorize ` +
    `the Hebrew verb infinitive "${infinitiveHe}"${translitPart}. ` +
    `It means "${meaning}" in English and belongs to the ` +
    `"${binyan}" binyan (conjugation family). ` +
    `The mnemonic must sound like the Hebrew word AND contain a clue ` +
    `to the binyan family: paal = simple/plain, ` +
    `piel = intensive/piercing, hifil = causative/he caused, ` +
    `nifal = passive/reflexive, hitpael = reflexive, ` +
    `pual = intensive passive, hofal = causative passive. ` +
    `Respond ONLY with the mnemonic text.`;
  const raw = await generate({prompt, signal});
  return raw.trim();
}

export async function generateHebrewVerbBinyanHint({
  infinitiveHe,
  transliteration,
  meaning,
  binyan,
  signal,
}) {
  const translitPart = transliteration ?
    ` (transliteration: "${transliteration}")` :
    '';
  const prompt =
    `Give a one-line binyan family hint for the Hebrew verb ` +
    `"${infinitiveHe}"${translitPart} meaning "${meaning}" ` +
    `in the ${binyan} binyan. ` +
    `Format: "Short English clue → ${binyan}" ` +
    `Examples: "He caused → hifil", "Piercing action → piel", ` +
    `"Plain action → paal", "Reflexive action → hitpael". ` +
    `Respond ONLY with the hint text.`;
  const raw = await generate({prompt, signal});
  return raw.trim();
}

export async function generateHebrewVerbSentences({
  infinitiveHe,
  transliteration,
  meaning,
  binyan,
  signal,
}) {
  const translitPart = transliteration ?
    ` (transliteration: "${transliteration}")` :
    '';
  const prompt =
    `Generate 3 short Modern Hebrew sentences using the Hebrew verb ` +
    `"${infinitiveHe}"${translitPart} which means "${meaning}" ` +
    `in English and belongs to the ${binyan} binyan. ` +
    `Use a different conjugation (past, present, future) in each sentence. ` +
    `The sentences must be in Modern Hebrew, not Arabic, Aramaic, or Yiddish. ` +
    `Keep each sentence simple (4-8 words). ` +
    `For each sentence provide: the Hebrew text, ` +
    `an English transliteration, and the English translation. ` +
    `Respond ONLY with JSON: ` +
    `{"sentences": [{"text": "...", "transliteration": "...", ` +
    `"translation": "..."}, ...]}`;
  const raw = await generate({prompt, signal});
  const parsed = extractJson(raw);
  if (parsed && Array.isArray(parsed.sentences)) return parsed.sentences;
  return [];
}

// ─── 4. Auto-tag ───────────────────────────────────────────────────

const AVAILABLE_TAGS = [
  'Adjective', 'Adverb', 'Business', 'Expression',
  'Food', 'General', 'Poetry', 'Verb',
];

export async function suggestTag({wordEn, wordFa, signal}) {
  const prompt =
    `Classify this vocabulary word into exactly one ` +
    `category from this list: ${AVAILABLE_TAGS.join(', ')}. ` +
    `English: "${wordEn}", Translation: "${wordFa}". ` +
    `Respond with ONLY the category name, nothing else.`;
  const raw = await generate({prompt, signal});
  const cleaned = raw.trim().replace(/[^a-zA-Z]/g, '');
  const match = AVAILABLE_TAGS.find((t) =>
    t.toLowerCase() === cleaned.toLowerCase(),
  );
  return match || 'General';
}

// ─── 5. Text-to-Speech ─────────────────────────────────────────────

export async function textToSpeech({text, signal}) {
  const res = await fetch(`${getAiApiBaseUrl()}/api/tts`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({text}),
    signal,
  });
  if (!res.ok) throw new Error(`TTS error: ${res.status}`);
  const blob = await res.blob();
  return blob; // audio/wav
}

// ─── 6. Speech-to-Text ─────────────────────────────────────────────

export async function speechToText({audioBlob, signal}) {
  const form = new FormData();
  form.append('audio_file', audioBlob, 'recording.webm');
  const res = await fetch(`${getAiApiBaseUrl()}/api/stt/transcribe`, {
    method: 'POST',
    body: form,
    signal,
  });
  if (!res.ok) throw new Error(`STT error: ${res.status}`);
  const data = await res.json();
  return data.text || '';
}

// ─── 7. Image generation ───────────────────────────────────────────

export async function startImageGen({prompt, signal}) {
  const res = await fetch(`${getAiApiBaseUrl()}/api/image-gen/start`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({prompt, steps: 20}),
    signal,
  });
  if (!res.ok) throw new Error(`Image gen start error: ${res.status}`);
  const data = await res.json();
  return data.jobId;
}

export async function pollImageGen({jobId, signal}) {
  const maxAttempts = 60;
  for (let i = 0; i < maxAttempts; i++) {
    const statusUrl =
      `${getAiApiBaseUrl()}/api/image-gen/status/${jobId}`;
    const res = await fetch(statusUrl, {signal});
    if (!res.ok) throw new Error(`Image gen poll error: ${res.status}`);
    const data = await res.json();
    if (data.status === 'complete' || data.imageUrl || data.image) {
      return data;
    }
    if (data.status === 'failed' || data.error) {
      throw new Error(data.error || 'Image generation failed');
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error('Image generation timed out');
}

export async function generateImage({prompt, signal}) {
  const jobId = await startImageGen({prompt, signal});
  return pollImageGen({jobId, signal});
}

// ─── 8. Conversation chat (streaming → collected) ──────────────────

export async function conversationChat({
  prompt,
  sessionId,
  useRag = false,
  signal,
}) {
  try {
    const res = await fetch(`${getAiApiBaseUrl()}/api/conversation-chat`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        prompt,
        sessionId,
        useRag,
      }),
      signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Chat error: ${res.status} ${body}`);
    }

    // Some providers may return plain text or buffered JSON without a stream.
    if (!res.body || typeof res.body.getReader !== 'function') {
      const text = await res.text();
      return text.trim();
    }

    // The endpoint returns NDJSON chunks.
    // Preserve incomplete lines between reads.
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';
    let done = false;

    const appendParsedLine = (line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed.response) fullText += parsed.response;
        if (parsed.message && parsed.message.content) {
          fullText += parsed.message.content;
        }
      } catch (_) {
        // Ignore non-JSON lines from streaming backends.
      }
    };

    while (!done) {
      const {value, done: readerDone} = await reader.read();
      done = readerDone;
      if (value) {
        buffer += decoder.decode(value, {stream: true});
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.forEach(appendParsedLine);
      }
    }

    if (buffer) {
      appendParsedLine(buffer);
    }

    if (fullText.trim()) {
      return fullText;
    }

    // Fallback for compatible backends that only implement /api/generate.
    return generate({prompt, signal, retries: 1});
  } catch (err) {
    // Fallback for backends without /api/conversation-chat.
    if (signal && signal.aborted) throw err;
    return generate({prompt, signal, retries: 1});
  }
}
